/**
 * The wizard's card surfaces, on the design system's tokens. These were the
 * CNC prototype's warm greys and its blue accent, hard-coded — which now reads
 * as a different product next to the brand green, and ignores dark mode.
 */

/** Graph-paper preview panel — an image, a toolpath and a document icon all sit
 * on the same backdrop. The grid lines are the border token, so the paper stays
 * paper in either theme. */
export const PREVIEW_SURFACE =
  'bg-muted bg-[linear-gradient(0deg,transparent_15px,hsl(var(--border))_16px),linear-gradient(90deg,transparent_15px,hsl(var(--border))_16px)] bg-[length:16px_16px]';

/** The system's focus glow, applied from the wizard root so the shared <Input>
 * stays untouched. The offset is zeroed because ring + offset + glow stacks
 * three rings on one field. */
export const WIZARD_FOCUS =
  '[&_input:focus-visible]:shadow-focus [&_input:focus-visible]:ring-offset-0 [&_textarea:focus-visible]:shadow-focus [&_textarea:focus-visible]:ring-offset-0';

/** Same shell as the shared <Card> (rounded-lg border bg-card shadow-sm), with
 * the hover lift the system prescribes for a clickable card. Kept as a class
 * string rather than the component because these wrap a preview + footer and
 * need `overflow-hidden` on the same element. */
export const CARD_SHELL =
  'rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden transition-[color,background-color,border-color,box-shadow] duration-fast hover:border-border-strong hover:shadow-md';

/** The shared <Badge> is 12px and pill-shaped; a file card's chip has to fit in
 * a 2.5rem strip, so it borrows the badge's tokens at a smaller scale rather
 * than inventing its own colours. */
export const META_BADGE =
  'inline-flex items-center rounded-full border border-transparent bg-secondary text-secondary-foreground px-1.5 py-[3px] text-[10px] font-semibold uppercase tracking-[0.03em]';

// Body font, not display: MuseoModerno's single-storey G reads as a Q, and the
// section titles here lead with "G-code".
export const SECTION_HEADING =
  'font-secondary text-sm font-semibold text-heading';

/** The one big thing on a step. Display face at the scale the done screen
 * already uses, so a step opens with a headline instead of another 14px row. */
export const STEP_HEADING =
  'font-primary text-3xl font-semibold tracking-tight text-heading mb-1.5';

/** Every overline in the wizard — BITS, G-CODE, SPINDLE SPEED, a form label.
 * One scale, and `muted-foreground` at full strength: these were 9–10px at 70%
 * opacity, which is below the contrast the system asks of any text. */
export const FIELD_LABEL =
  'text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';

/** Anything that explains rather than labels — an empty state, a caption, a
 * count. Body size, so it reads as text and not as fine print. */
export const HINT = 'text-xs text-muted-foreground';

export const DOC_ICON = 'w-9 h-9 text-muted-foreground/45';

/** The label on a pickable tile — Job details' machine features, the listing
 * type. Shared so the two sets of tiles read as the same control. */
export const OPTION_KEY =
  'font-tertiary text-[13px] font-medium text-foreground';

/** The delete chip on a card corner. Quiet until pointed at: a solid red dot on
 * every card competes with the thumbnail it sits on, and reads as a warning
 * about the file rather than as the way to remove it. */
// Scrim, not a light chip: this sits on the user's own photo, and a pale one
// vanishes on anything bright.
export const DELETE_BADGE =
  'absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-neutral-900/55 text-white backdrop-blur-sm flex items-center justify-center shadow-sm transition-colors duration-fast hover:bg-destructive';
